import { ShipTier } from "@voidex/contracts-ts";
import { formatUnits, maxUint256 } from "viem";
import { useReadContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { contractConfigs } from "../web3/contracts";

export function useUsdcBalance(address?: `0x${string}`) {
	const { data, isLoading, error, refetch } = useReadContract({
		...contractConfigs.usdc,
		functionName: "balanceOf",
		args: address ? [address] : undefined,
		query: {
			enabled: !!address,
			refetchInterval: 10_000,
		},
	});

	return {
		balance: data ?? 0n,
		formattedBalance: data ? formatUnits(data, 6) : "0",
		isLoading,
		error,
		refetch,
	};
}

export function useShipPrices() {
	const { data: probePrice, isLoading: isProbeLoading } = useReadContract({
		...contractConfigs.shipShop,
		functionName: "tierPrice",
		args: [ShipTier.Probe],
	});

	const { data: frigatePrice, isLoading: isFrigateLoading } = useReadContract({
		...contractConfigs.shipShop,
		functionName: "tierPrice",
		args: [ShipTier.Frigate],
	});

	const { data: dreadnoughtPrice, isLoading: isDreadnoughtLoading } = useReadContract({
		...contractConfigs.shipShop,
		functionName: "tierPrice",
		args: [ShipTier.Dreadnought],
	});

	return {
		probePrice: probePrice ?? 0n,
		frigatePrice: frigatePrice ?? 0n,
		dreadnoughtPrice: dreadnoughtPrice ?? 0n,
		formattedProbePrice: probePrice ? formatUnits(probePrice, 6) : "0",
		formattedFrigatePrice: frigatePrice ? formatUnits(frigatePrice, 6) : "0",
		formattedDreadnoughtPrice: dreadnoughtPrice ? formatUnits(dreadnoughtPrice, 6) : "0",
		isLoading: isProbeLoading || isFrigateLoading || isDreadnoughtLoading,
	};
}

export function useBuyShip() {
	const { writeContractAsync, data: hash, isPending, error, reset } = useWriteContract();
	const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
		hash,
	});

	const buyShip = async (tier: ShipTier) => {
		return writeContractAsync({
			...contractConfigs.shipShop,
			functionName: "buyShip",
			args: [tier],
		});
	};

	return {
		buyShip,
		hash,
		isPending: isPending || isConfirming,
		isSuccess,
		error,
		reset,
	};
}

export function useUsdcAllowanceForShop(owner?: `0x${string}`) {
	const { data, isLoading, refetch } = useReadContract({
		...contractConfigs.usdc,
		functionName: "allowance",
		args: owner ? [owner, contractConfigs.shipShop.address] : undefined,
		query: {
			enabled: !!owner,
			refetchInterval: 6_000,
		},
	});

	return {
		allowance: data ?? 0n,
		formattedAllowance: data ? formatUnits(data, 6) : "0",
		isLoading,
		refetch,
	};
}

export function useApproveUsdcForShop() {
	const { writeContractAsync, data: hash, isPending, error, reset } = useWriteContract();
	const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
		hash,
	});

	const approve = async () => {
		return writeContractAsync({
			...contractConfigs.usdc,
			functionName: "approve",
			args: [contractConfigs.shipShop.address, maxUint256],
		});
	};

	return {
		approve,
		hash,
		isPending: isPending || isConfirming,
		isSuccess,
		error,
		reset,
	};
}
